import React, { useEffect, useMemo, useState } from 'react';
import {
  Clock,
  DownloadSimple,
  FileArrowUp,
  PaperPlaneTilt,
  Pause,
  PencilSimple,
  Play,
  Plus,
  ShieldCheck,
  Trash,
  UploadSimple,
  XCircle,
} from '@phosphor-icons/react';
import { api } from '@/lib/api';
import { apiErrorMessage, fmtDate, parsePhoneList } from '@/lib/utils';
import { Badge, Button, DateTimePicker, Input, Label, Spinner, Textarea, useToast } from '@/components/ui-kit';
import { useInstances } from '@/context/InstanceContext';

const EMPTY_DRAFT = {
  type: 'text',
  text: '',
  media: '',
  caption: '',
  fileName: '',
  mimetype: '',
  mediatype: 'image',
};

const TYPE_OPTIONS = [
  { id: 'text', label: 'Texto' },
  { id: 'image', label: 'Imagem' },
  { id: 'video', label: 'Vídeo' },
  { id: 'audio', label: 'Áudio' },
  { id: 'document', label: 'Arquivo' },
];

function normalizeItem(draft) {
  if (draft.type === 'text') {
    const text = draft.text.trim();
    return text ? { type: 'text', text } : null;
  }
  if (!draft.media.trim()) return null;
  if (draft.type === 'audio') {
    return {
      type: 'audio',
      media: draft.media.trim(),
      caption: draft.caption.trim(),
      fileName: draft.fileName,
      mimetype: draft.mimetype,
    };
  }
  return {
    type: 'media',
    mediatype: draft.type === 'document' ? 'document' : draft.type,
    media: draft.media.trim(),
    caption: draft.caption.trim(),
    fileName: draft.fileName,
    mimetype: draft.mimetype,
  };
}

function itemTitle(item) {
  if (item.type === 'text') return 'Texto';
  if (item.type === 'audio') return 'Áudio';
  if (item.mediatype === 'video') return 'Vídeo';
  if (item.mediatype === 'document') return 'Arquivo';
  return 'Imagem';
}

function firstLine(item) {
  return item.text || item.caption || item.fileName || 'Mídia anexada';
}

function localNowIso() {
  const date = new Date();
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function statusLabel(status) {
  const labels = {
    pending: 'Agendado',
    running: 'Rodando',
    paused: 'Pausado',
    done: 'Concluído',
    pending_confirmation: 'Pendente confirmação',
    failed: 'Falhou',
    cancelled: 'Cancelado',
  };
  return labels[status] || status || '—';
}

function statusTone(status) {
  if (status === 'done') return 'success';
  if (status === 'failed' || status === 'cancelled') return 'danger';
  if (status === 'paused' || status === 'pending' || status === 'pending_confirmation') return 'warn';
  return 'info';
}

function broadcastStatusLabel(row) {
  const pending = Number(row?.pendingTargets || 0);
  if (row?.status === 'done' && pending > 0) return 'Pendente confirmação';
  return statusLabel(row?.status);
}

function broadcastStatusTone(row) {
  const pending = Number(row?.pendingTargets || 0);
  if (row?.status === 'done' && pending > 0) return 'warn';
  return statusTone(row?.status);
}

function localDateTimeFromIso(value) {
  if (!value) return '';
  try {
    const date = new Date(value);
    const offsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
  } catch {
    return '';
  }
}

function normalizePhoneKey(value) {
  return String(value || '').replace(/\D+/g, '');
}

export default function BroadcastPage() {
  const { instances, current } = useInstances();
  const [selectedInstance, setSelectedInstance] = useState('');
  const [rawPhones, setRawPhones] = useState('');
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [items, setItems] = useState([]);
  const [minDelay, setMinDelay] = useState(60);
  const [maxDelay, setMaxDelay] = useState(120);
  const [batchSize, setBatchSize] = useState(100);
  const [batchPause, setBatchPause] = useState(30);
  const [windowStart, setWindowStart] = useState('08:00');
  const [windowEnd, setWindowEnd] = useState('20:00');
  const [scheduledAt, setScheduledAt] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loadingBroadcasts, setLoadingBroadcasts] = useState(false);
  const [broadcastBases, setBroadcastBases] = useState([]);
  const [loadingBases, setLoadingBases] = useState(false);
  const [savingBase, setSavingBase] = useState(false);
  const [selectedBaseId, setSelectedBaseId] = useState('');
  const [excludedBaseIds, setExcludedBaseIds] = useState([]);
  const [baseName, setBaseName] = useState('');
  const [activeView, setActiveView] = useState('list');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [actingId, setActingId] = useState(null);
  const toast = useToast();

  const phones = useMemo(() => Array.from(new Set(parsePhoneList(rawPhones))), [rawPhones]);
  const activeInstance = selectedInstance || current || instances[0]?.instanceName || '';
  const selectedBase = useMemo(
    () => broadcastBases.find((base) => base.id === selectedBaseId) || null,
    [broadcastBases, selectedBaseId],
  );
  const availableExclusionBases = useMemo(
    () => broadcastBases.filter((base) => base.id !== selectedBaseId),
    [broadcastBases, selectedBaseId],
  );
  const excludedBases = useMemo(
    () => broadcastBases.filter((base) => excludedBaseIds.includes(base.id)),
    [broadcastBases, excludedBaseIds],
  );
  const excludedPhoneKeys = useMemo(() => {
    const keys = new Set();
    excludedBases.forEach((base) => {
      (base.phones || []).forEach((phone) => {
        const key = normalizePhoneKey(phone);
        if (key) keys.add(key);
      });
    });
    return keys;
  }, [excludedBases]);
  const filteredPhones = useMemo(() => {
    if (!excludedPhoneKeys.size) return phones;
    return phones.filter((phone) => !excludedPhoneKeys.has(normalizePhoneKey(phone)));
  }, [phones, excludedPhoneKeys]);
  const excludedTargetsCount = Math.max(0, phones.length - filteredPhones.length);
  const currentDraftItem = normalizeItem(draft);
  const finalItems = currentDraftItem ? [...items, currentDraftItem] : items;
  const estimatedMinutes = useMemo(() => {
    if (!filteredPhones.length) return 0;
    const averageDelay = (Number(minDelay) + Number(maxDelay)) / 2;
    const pauses = Math.max(0, Math.floor((filteredPhones.length - 1) / Math.max(1, Number(batchSize) || 1)));
    return Math.round(((filteredPhones.length - 1) * averageDelay + pauses * Number(batchPause) * 60) / 60);
  }, [filteredPhones.length, minDelay, maxDelay, batchSize, batchPause]);

  const refreshBroadcasts = async () => {
    setLoadingBroadcasts(true);
    try {
      const rows = await api.listDispatches({ limit: 100 });
      setBroadcasts(rows.filter((row) => row.targetKind === 'lead' && !row.campaignId));
    } catch (error) {
      toast.show(apiErrorMessage(error, 'Erro ao carregar broadcasts'), 'error');
    } finally {
      setLoadingBroadcasts(false);
    }
  };

  const refreshBroadcastBases = async () => {
    setLoadingBases(true);
    try {
      const rows = await api.listBroadcastBases();
      setBroadcastBases(rows);
      setExcludedBaseIds((prev) => prev.filter((id) => rows.some((base) => base.id === id)));
    } catch (error) {
      toast.show(apiErrorMessage(error, 'Erro ao carregar bases salvas'), 'error');
    } finally {
      setLoadingBases(false);
    }
  };

  useEffect(() => {
    refreshBroadcasts();
    refreshBroadcastBases();
    const timer = setInterval(refreshBroadcasts, 8000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!instances.length) {
      setSelectedInstance('');
      return;
    }
    setSelectedInstance((prev) => {
      if (prev && instances.some((instance) => instance.instanceName === prev)) return prev;
      const open = instances.find((instance) => instance.connectionStatus === 'open');
      return current || open?.instanceName || instances[0]?.instanceName || '';
    });
  }, [instances, current]);

  const setDraftType = (type) => {
    setDraft((prev) => ({
      ...prev,
      type,
      mediatype: type === 'document' ? 'document' : type === 'audio' ? undefined : type,
    }));
  };

  const handleBaseFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    setRawPhones((prev) => [prev, text].filter(Boolean).join('\n'));
    if (!baseName) setBaseName(file.name.replace(/\.[^.]+$/, ''));
    toast.show('Base adicionada ao broadcast.', 'success');
  };

  const applyBroadcastBase = (base) => {
    if (!base) {
      setSelectedBaseId('');
      setExcludedBaseIds([]);
      setBaseName('');
      return;
    }
    setSelectedBaseId(base.id);
    setExcludedBaseIds((prev) => prev.filter((id) => id !== base.id));
    setBaseName(base.name || '');
    setRawPhones((base.phones || []).join('\n'));
    if (base.instanceName) setSelectedInstance(base.instanceName);
  };

  const saveBroadcastBase = async () => {
    if (!phones.length) {
      toast.show('Adicione telefones válidos antes de salvar a base.', 'error');
      return;
    }
    const name = baseName.trim();
    if (!name) {
      toast.show('Informe um nome para a base.', 'error');
      return;
    }
    setSavingBase(true);
    try {
      const payload = {
        name,
        instanceName: activeInstance,
        phones,
      };
      const saved = selectedBaseId
        ? await api.updateBroadcastBase(selectedBaseId, payload)
        : await api.createBroadcastBase(payload);
      await refreshBroadcastBases();
      setSelectedBaseId(saved.id);
      setBaseName(saved.name || name);
      toast.show(selectedBaseId ? 'Base atualizada.' : 'Base salva para reutilizar.', 'success');
    } catch (error) {
      toast.show(apiErrorMessage(error, 'Erro ao salvar base'), 'error');
    } finally {
      setSavingBase(false);
    }
  };

  const removeBroadcastBase = async () => {
    if (!selectedBaseId) return;
    const selectedBase = broadcastBases.find((base) => base.id === selectedBaseId);
    const selectedName = selectedBase?.name || baseName || 'base selecionada';
    const selectedTotal = selectedBase?.totalPhones || selectedBase?.phones?.length || phones.length || 0;
    const confirmed = window.confirm(
      `Excluir definitivamente a base "${selectedName}" com ${selectedTotal} contatos?\n\n` +
      'Essa ação remove a lista salva do sistema. Para apenas não enviar para esses contatos, use Bases de exclusão.'
    );
    if (!confirmed) return;
    const removedId = selectedBaseId;
    setSavingBase(true);
    try {
      await api.deleteBroadcastBase(removedId);
      setSelectedBaseId('');
      setExcludedBaseIds((prev) => prev.filter((id) => id !== removedId));
      setBaseName('');
      await refreshBroadcastBases();
      toast.show('Base salva excluída.', 'success');
    } catch (error) {
      toast.show(apiErrorMessage(error, 'Erro ao remover base'), 'error');
    } finally {
      setSavingBase(false);
    }
  };

  const toggleExcludedBase = (baseId) => {
    setExcludedBaseIds((prev) => (
      prev.includes(baseId) ? prev.filter((id) => id !== baseId) : [...prev, baseId]
    ));
  };

  const handleMediaFile = async (file) => {
    if (!file) return;
    const kind = draft.type;
    if (kind === 'video' && file.size > 100 * 1024 * 1024) {
      toast.show('Vídeo precisa ter no máximo 100MB.', 'error');
      return;
    }
    if (kind === 'audio' && !/\.ogg$/i.test(file.name)) {
      toast.show('Para áudio no WhatsApp, use arquivo .ogg.', 'error');
      return;
    }
    if (kind === 'video' && !/\.mp4$/i.test(file.name)) {
      toast.show('Para vídeo, use arquivo .mp4.', 'error');
      return;
    }
    if (kind === 'image' && !/\.(jpe?g|png)$/i.test(file.name)) {
      toast.show('Para imagem, use JPG, JPEG ou PNG.', 'error');
      return;
    }
    setUploading(true);
    try {
      const uploaded = await api.uploadMedia(file);
      setDraft((prev) => ({
        ...prev,
        media: uploaded.url || uploaded.dataUrl || uploaded.media || '',
        fileName: file.name,
        mimetype: file.type,
      }));
      toast.show('Arquivo carregado.', 'success');
    } catch (error) {
      toast.show(apiErrorMessage(error, 'Erro ao subir arquivo'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const addDraft = () => {
    if (!currentDraftItem) {
      toast.show('Preencha o conteúdo antes de adicionar à sequência.', 'error');
      return;
    }
    setItems((prev) => [...prev, currentDraftItem]);
    setDraft(EMPTY_DRAFT);
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const runDispatchAction = async (id, action) => {
    setActingId(id);
    try {
      if (action === 'pause') await api.pauseDispatch(id);
      if (action === 'resume') await api.resumeDispatch(id);
      if (action === 'cancel') await api.cancelDispatch(id);
      toast.show('Broadcast atualizado.', 'success');
      await refreshBroadcasts();
    } catch (error) {
      toast.show(apiErrorMessage(error, 'Erro ao atualizar broadcast'), 'error');
    } finally {
      setActingId(null);
    }
  };

  const downloadDispatchExport = async (row) => {
    try {
      const response = await fetch(api.exportDispatchUrl(row.id));
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Erro ao exportar broadcast');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const safeName = String(row.broadcastBaseName || row.id || 'broadcast')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9._-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 70);
      const link = document.createElement('a');
      link.href = url;
      link.download = `broadcast-${safeName || row.id}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.show('Exportação gerada.', 'success');
    } catch (error) {
      toast.show(error.message || 'Erro ao exportar broadcast', 'error');
    }
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setEditForm({
      items: (row.items || []).map((item) => ({
        ...item,
        media: item.media || item.mediaPreview || '',
      })),
      scheduledAt: localDateTimeFromIso(row.scheduledAt),
      minDelaySeconds: row.minDelaySeconds ?? 60,
      maxDelaySeconds: row.maxDelaySeconds ?? 120,
      batchSize: row.batchSize ?? 100,
      batchPauseMinutes: row.batchPauseMinutes ?? 30,
      windowStart: row.windowStart || '08:00',
      windowEnd: row.windowEnd || '20:00',
    });
  };

  const updateEditItem = (index, patch) => {
    setEditForm((prev) => ({
      ...prev,
      items: (prev.items || []).map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
    }));
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setActingId(editingId);
    try {
      await api.updateDispatch(editingId, {
        scheduledAt: editForm.scheduledAt ? new Date(editForm.scheduledAt).toISOString() : undefined,
        minDelaySeconds: Number(editForm.minDelaySeconds),
        maxDelaySeconds: Number(editForm.maxDelaySeconds),
        batchSize: Number(editForm.batchSize),
        batchPauseMinutes: Number(editForm.batchPauseMinutes),
        windowStart: editForm.windowStart,
        windowEnd: editForm.windowEnd,
        items: editForm.items,
      });
      setEditingId(null);
      toast.show('Regras do broadcast salvas.', 'success');
      await refreshBroadcasts();
    } catch (error) {
      toast.show(apiErrorMessage(error, 'Erro ao salvar broadcast'), 'error');
    } finally {
      setActingId(null);
    }
  };

  const submit = async () => {
    if (!activeInstance) {
      toast.show('Conecte uma instância antes de criar o broadcast.', 'error');
      return;
    }
    if (!phones.length) {
      toast.show('Adicione pelo menos um telefone válido.', 'error');
      return;
    }
    if (!filteredPhones.length) {
      toast.show('Todos os telefones foram removidos pelas bases de exclusão.', 'error');
      return;
    }
    if (!finalItems.length) {
      toast.show('Adicione pelo menos uma mensagem.', 'error');
      return;
    }
    if (Number(minDelay) < 60 || Number(maxDelay) < Number(minDelay)) {
      toast.show('Use delay mínimo de 60s e máximo maior que o mínimo.', 'error');
      return;
    }

    const first = finalItems[0];
    const targets = filteredPhones.map((phone) => ({ groupJid: `${phone}@s.whatsapp.net`, subject: phone }));
    setSubmitting(true);
    try {
      await api.schedule({
        instanceName: activeInstance,
        targets,
        campaignId: null,
        type: first.type,
        text: first.text,
        mediatype: first.mediatype,
        media: first.media,
        caption: first.caption,
        fileName: first.fileName,
        mimetype: first.mimetype,
        items: finalItems,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : new Date().toISOString(),
        minDelaySeconds: Number(minDelay),
        maxDelaySeconds: Number(maxDelay),
        batchSize: Number(batchSize),
        batchPauseMinutes: Number(batchPause),
        windowStart,
        windowEnd,
        broadcastBaseId: selectedBaseId || null,
        broadcastBaseName: selectedBase?.name || baseName.trim() || null,
        excludedBroadcastBaseIds: excludedBaseIds,
        excludedBroadcastBaseNames: excludedBases.map((base) => base.name || base.id),
        excludedTargetCount: excludedTargetsCount,
        originalTargetCount: phones.length,
      });
      setItems([]);
      setDraft(EMPTY_DRAFT);
      setScheduledAt('');
      setExcludedBaseIds([]);
      setActiveView('list');
      toast.show('Broadcast criado. O envio vai rodar em segundo plano.', 'success');
      await refreshBroadcasts();
    } catch (error) {
      toast.show(apiErrorMessage(error, 'Erro ao criar broadcast'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-w-0" data-testid="page-broadcast">
      {toast.node}
      <div className="flex min-w-0 flex-col items-start justify-between gap-6 lg:flex-row">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <span className="h-px w-7" style={{ background: 'var(--s1-accent)' }} />
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: 'var(--s1-accent-text)' }}>
              Broadcast 1x1
            </span>
          </div>
          <h1 className="mt-5 font-display text-4xl font-normal leading-[1.05] sm:text-[42px]" style={{ color: 'var(--s1-text)' }}>
            Disparo seguro para listas
          </h1>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed" style={{ color: 'var(--s1-text-2)' }}>
            Envie sequências no privado usando uma instância conectada, com intervalo aleatório, pausa por lote e janela de horário.
          </p>
        </div>
        <div className="mt-2 flex w-full flex-wrap items-center gap-2 sm:w-auto lg:justify-end">
          <Badge variant="warn">opt-in recomendado</Badge>
          <Button type="button" onClick={() => setActiveView('create')}>
            <Plus size={16} /> Novo broadcast
          </Button>
        </div>
      </div>

      <div className="mt-7 flex flex-wrap gap-2 border-b pb-2" style={{ borderColor: 'var(--s1-line)' }}>
        {[
          ['list', 'Acompanhamento'],
          ['create', 'Novo broadcast'],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setActiveView(id)}
            className="rounded-[var(--s1-radius-nav)] px-4 py-2 text-sm font-semibold transition-colors"
            style={{
              background: activeView === id ? 'var(--s1-accent-soft)' : 'transparent',
              color: activeView === id ? 'var(--s1-accent-text)' : 'var(--s1-text-2)',
              border: activeView === id ? '1px solid var(--s1-accent)' : '1px solid transparent',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {activeView === 'list' && (
      <section className="s1-card mt-8 min-w-0 rounded-[var(--s1-radius-card)] border p-4 sm:p-6" style={{ background: 'var(--s1-card)', borderColor: 'var(--s1-card-border)' }}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-display text-[24px] font-medium" style={{ color: 'var(--s1-text)' }}>Painel de broadcasts</h2>
            <p className="mt-1 text-sm" style={{ color: 'var(--s1-muted)' }}>Acompanhe andamento, falhas e próximas execuções dos disparos 1x1.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="secondary" onClick={refreshBroadcasts} disabled={loadingBroadcasts}>
              {loadingBroadcasts ? <Spinner /> : <Clock size={16} />} Atualizar
            </Button>
            <Button type="button" onClick={() => setActiveView('create')}>
              <Plus size={16} /> Criar
            </Button>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          {broadcasts.length === 0 ? (
            <div className="rounded-[var(--s1-radius-inner)] border px-4 py-6 text-center text-sm" style={{ borderColor: 'var(--s1-inner-border)', color: 'var(--s1-muted)' }}>
              Nenhum broadcast criado ainda.
            </div>
          ) : (
            broadcasts.map((row) => {
              const total = Number(row.totalTargets || row.targets?.length || 0);
              const sent = Number(row.sentTargets || 0);
              const pending = Number(row.pendingTargets || 0);
              const failed = Number(row.failedTargets || 0);
              const completed = Math.max(Number(row.completedTargets || 0), sent + pending + failed);
              const remaining = Math.max(0, total - completed);
              const percent = total ? Math.round((completed / total) * 100) : 0;
              const canPause = row.status === 'pending' || row.status === 'running';
              const canResume = row.status === 'paused';
              const canEdit = ['pending', 'running', 'paused'].includes(row.status);
              const canCancel = ['pending', 'running', 'paused'].includes(row.status);
              return (
                <div key={row.id} className="min-w-0 rounded-[var(--s1-radius-inner)] border p-4" style={{ background: 'var(--s1-inner)', borderColor: 'var(--s1-inner-border)' }}>
                  <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-[1.2fr_minmax(0,1.6fr)_auto]">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={broadcastStatusTone(row)}>{broadcastStatusLabel(row)}</Badge>
                        {row.broadcastBaseName && <Badge>{row.broadcastBaseName}</Badge>}
                        {Number(row.excludedTargetCount || 0) > 0 && (
                          <Badge variant="warn">{Number(row.excludedTargetCount)} excluídos</Badge>
                        )}
                        {(row.excludedBroadcastBaseNames || []).length > 0 && (
                          <Badge variant="info">exclui: {(row.excludedBroadcastBaseNames || []).join(', ')}</Badge>
                        )}
                        <span className="break-all font-mono text-xs" style={{ color: 'var(--s1-muted)' }}>{row.id}</span>
                      </div>
                      <p className="mt-2 text-sm font-semibold" style={{ color: 'var(--s1-text)' }}>
                        {row.itemsCount || row.items?.length || 1} parte(s) · {total} contatos
                      </p>
                      <p className="mt-1 text-xs" style={{ color: 'var(--s1-muted)' }}>
                        Próximo: {row.nextRunAt ? fmtDate(row.nextRunAt) : row.status === 'done' ? 'concluído' : '—'}
                        {row.waitReason ? ` · ${row.waitReason}` : ''}
                      </p>
                    </div>

                    <div className="min-w-0">
                      {row.items?.[0] && (
                        <div className="mb-3 min-w-0 overflow-hidden rounded-[var(--s1-radius-inner)] border p-3" style={{ borderColor: 'var(--s1-inner-border)', background: 'var(--s1-card)' }}>
                          {row.items[0].type === 'text' ? (
                            <p className="line-clamp-2 break-words text-sm" style={{ color: 'var(--s1-text-2)', overflowWrap: 'anywhere' }}>“{row.items[0].text || firstLine(row.items[0])}”</p>
                          ) : (
                            <div className="grid min-w-0 grid-cols-1 items-center gap-3 sm:grid-cols-[92px_1fr]">
                              <MediaThumb item={row.items[0]} />
                              <div className="min-w-0">
                                <p className="text-sm font-semibold" style={{ color: 'var(--s1-text)' }}>{itemTitle(row.items[0])}</p>
                                <p className="break-words text-xs font-mono" style={{ color: 'var(--s1-muted)', overflowWrap: 'anywhere' }}>{row.items[0].fileName || firstLine(row.items[0])}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-5">
                        <MiniStat label="Enviados" value={sent} />
                        <MiniStat label="Pendentes" value={pending} tone={pending ? 'warn' : 'default'} />
                        <MiniStat label="Faltam" value={remaining} />
                        <MiniStat label="Erros" value={failed} tone={failed ? 'danger' : 'default'} />
                        <MiniStat label="Progresso" value={`${percent}%`} />
                      </div>
                      <div className="mt-3 h-2 overflow-hidden rounded-full" style={{ background: 'var(--s1-field)' }}>
                        <div className="h-full rounded-full" style={{ width: `${percent}%`, background: 'var(--s1-accent)' }} />
                      </div>
                      {row.lastError && (
                        <p className="mt-2 line-clamp-2 text-xs" style={{ color: '#f87171' }}>
                          Último erro: {typeof row.lastError === 'string' ? row.lastError : JSON.stringify(row.lastError)}
                        </p>
                      )}
                      {pending > 0 && (
                        <p
                          className="mt-2 rounded-[var(--s1-radius-inner)] border px-3 py-2 text-xs"
                          style={{
                            color: 'var(--s1-warning)',
                            borderColor: 'rgba(245, 158, 11, 0.35)',
                            background: 'rgba(245, 158, 11, 0.08)',
                          }}
                        >
                          {pending} contato(s) ficaram pendentes de confirmação na Evolution. Eles podem aparecer no Inbox como tentativa de envio, mas ainda não há confirmação real no WhatsApp.
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap items-start gap-2 xl:justify-end">
                      {canPause && (
                        <Button type="button" variant="secondary" size="sm" onClick={() => runDispatchAction(row.id, 'pause')} disabled={actingId === row.id}>
                          <Pause size={15} /> Pausar
                        </Button>
                      )}
                      {canResume && (
                        <Button type="button" variant="secondary" size="sm" onClick={() => runDispatchAction(row.id, 'resume')} disabled={actingId === row.id}>
                          <Play size={15} /> Retomar
                        </Button>
                      )}
                      {canEdit && (
                        <Button type="button" variant="secondary" size="sm" onClick={() => openEdit(row)} disabled={actingId === row.id}>
                          <PencilSimple size={15} /> Editar
                        </Button>
                      )}
                      <Button type="button" variant="secondary" size="sm" onClick={() => downloadDispatchExport(row)}>
                        <DownloadSimple size={15} /> Exportar
                      </Button>
                      {canCancel && (
                        <Button type="button" variant="danger" size="sm" onClick={() => runDispatchAction(row.id, 'cancel')} disabled={actingId === row.id}>
                          <XCircle size={15} /> Cancelar
                        </Button>
                      )}
                    </div>
                  </div>

                  {editingId === row.id && (
                    <div className="mt-4 space-y-4 border-t pt-4" style={{ borderColor: 'var(--s1-line-soft)' }}>
                      <div>
                        <Label>Conteúdo do disparo</Label>
                        <div className="space-y-3">
                          {(editForm.items || []).map((item, index) => (
                            <div key={`${item.type || 'item'}-${index}`} className="rounded-[var(--s1-radius-inner)] border p-3" style={{ borderColor: 'var(--s1-inner-border)', background: 'var(--s1-card)' }}>
                              <div className="mb-2 flex items-center gap-2">
                                <Badge>{index + 1}</Badge>
                                <span className="text-sm font-semibold" style={{ color: 'var(--s1-text)' }}>{itemTitle(item)}</span>
                              </div>
                              {item.type === 'text' ? (
                                <Textarea
                                  rows={3}
                                  value={item.text || ''}
                                  onChange={(e) => updateEditItem(index, { text: e.target.value })}
                                  style={{ background: 'var(--s1-field)', borderColor: 'var(--s1-field-border)', color: 'var(--s1-text)' }}
                                />
                              ) : (
                                <div className="grid gap-3 md:grid-cols-[120px_1fr]">
                                  <MediaThumb item={item} />
                                  <div className="space-y-2">
                                    <Input
                                      value={item.media || item.mediaPreview || ''}
                                      onChange={(e) => updateEditItem(index, { media: e.target.value })}
                                      placeholder="URL/base64 da mídia"
                                    />
                                    <Textarea
                                      rows={2}
                                      value={item.caption || item.text || ''}
                                      onChange={(e) => updateEditItem(index, { caption: e.target.value, text: item.type === 'media' ? e.target.value : item.text })}
                                      placeholder="Legenda opcional"
                                      style={{ background: 'var(--s1-field)', borderColor: 'var(--s1-field-border)', color: 'var(--s1-text)' }}
                                    />
                                    <p className="text-xs font-mono" style={{ color: 'var(--s1-muted)' }}>{item.fileName || item.mimetype || 'mídia'}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
                      <div className="lg:col-span-2">
                        <Label>Início</Label>
                        <Input type="datetime-local" value={editForm.scheduledAt || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, scheduledAt: e.target.value }))} />
                      </div>
                      <div>
                        <Label>Min. s</Label>
                        <Input type="number" min="60" value={editForm.minDelaySeconds || 60} onChange={(e) => setEditForm((prev) => ({ ...prev, minDelaySeconds: e.target.value }))} />
                      </div>
                      <div>
                        <Label>Máx. s</Label>
                        <Input type="number" min="60" value={editForm.maxDelaySeconds || 120} onChange={(e) => setEditForm((prev) => ({ ...prev, maxDelaySeconds: e.target.value }))} />
                      </div>
                      <div>
                        <Label>Lote</Label>
                        <Input type="number" min="1" value={editForm.batchSize || 100} onChange={(e) => setEditForm((prev) => ({ ...prev, batchSize: e.target.value }))} />
                      </div>
                      <div>
                        <Label>Pausa</Label>
                        <Input type="number" min="0" value={editForm.batchPauseMinutes || 30} onChange={(e) => setEditForm((prev) => ({ ...prev, batchPauseMinutes: e.target.value }))} />
                      </div>
                      <div className="grid grid-cols-2 gap-2 lg:col-span-2">
                        <div>
                          <Label>Começa</Label>
                          <Input type="time" value={editForm.windowStart || '08:00'} onChange={(e) => setEditForm((prev) => ({ ...prev, windowStart: e.target.value }))} />
                        </div>
                        <div>
                          <Label>Para</Label>
                          <Input type="time" value={editForm.windowEnd || '20:00'} onChange={(e) => setEditForm((prev) => ({ ...prev, windowEnd: e.target.value }))} />
                        </div>
                      </div>
                      <div className="flex items-end gap-2 lg:col-span-7">
                        <Button type="button" onClick={saveEdit} disabled={actingId === row.id}>
                          {actingId === row.id ? <Spinner /> : <PencilSimple size={15} />} Salvar alterações
                        </Button>
                        <Button type="button" variant="ghost" onClick={() => setEditingId(null)}>
                          Fechar
                        </Button>
                      </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>
      )}

      {activeView === 'create' && (
      <div className="mt-8 grid min-w-0 grid-cols-1 gap-5 xl:grid-cols-[0.9fr_minmax(0,1.15fr)]">
        <section className="s1-card min-w-0 rounded-[var(--s1-radius-card)] border p-4 sm:p-6" style={{ background: 'var(--s1-card)', borderColor: 'var(--s1-card-border)' }}>
          <h2 className="font-display text-[24px] font-medium" style={{ color: 'var(--s1-text)' }}>Base e segurança</h2>
          <div className="mt-5 space-y-5">
            <div>
              <Label>Instância de envio</Label>
              <select
                value={activeInstance}
                onChange={(event) => setSelectedInstance(event.target.value)}
                className="s1-field w-full rounded-[var(--s1-radius-nav)] border px-4 py-3 text-sm font-semibold outline-none"
                style={{ background: 'var(--s1-field)', borderColor: 'var(--s1-field-border)', color: 'var(--s1-text)' }}
              >
                {instances.map((instance) => (
                  <option key={instance.instanceName} value={instance.instanceName}>
                    {instance.profileName || instance.instanceName}
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded-[var(--s1-radius-inner)] border p-4" style={{ background: 'var(--s1-inner)', borderColor: 'var(--s1-inner-border)' }}>
              <div className="flex items-center justify-between gap-3">
                <Label>Base salva</Label>
                <Badge>{loadingBases ? 'carregando' : `${broadcastBases.length} bases`}</Badge>
              </div>
              <select
                value={selectedBaseId}
                onChange={(event) => {
                  const base = broadcastBases.find((row) => row.id === event.target.value);
                  applyBroadcastBase(base || null);
                }}
                className="s1-field mt-2 w-full rounded-[var(--s1-radius-nav)] border px-4 py-3 text-sm font-semibold outline-none"
                style={{ background: 'var(--s1-field)', borderColor: 'var(--s1-field-border)', color: 'var(--s1-text)' }}
              >
                <option value="">Sem base selecionada</option>
                {broadcastBases.map((base) => (
                  <option key={base.id} value={base.id}>
                    {base.name} · {base.totalPhones || base.phones?.length || 0} leads
                  </option>
                ))}
              </select>
              <div className="mt-3">
                <Label>Nome da base</Label>
                <Input
                  value={baseName}
                  onChange={(event) => setBaseName(event.target.value)}
                  placeholder="Ex: Viveiro Santo Amaro"
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={saveBroadcastBase} disabled={savingBase}>
                  {savingBase ? <Spinner /> : <Plus size={15} />}
                  {selectedBaseId ? 'Atualizar base' : 'Salvar base'}
                </Button>
                {selectedBaseId && (
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={removeBroadcastBase}
                    disabled={savingBase}
                    title="Exclui definitivamente a base salva do sistema"
                  >
                    <Trash size={15} /> Excluir base salva
                  </Button>
                )}
              </div>
              <p className="mt-3 text-xs leading-relaxed" style={{ color: 'var(--s1-muted)' }}>
                Salve a lista uma vez e reutilize em novos broadcasts sem carregar CSV de novo.
              </p>
            </div>

            {availableExclusionBases.length > 0 && (
              <div className="rounded-[var(--s1-radius-inner)] border p-4" style={{ background: 'var(--s1-inner)', borderColor: 'var(--s1-inner-border)' }}>
                <div className="flex items-center justify-between gap-3">
                  <Label>Bases de exclusão</Label>
                  <Badge variant={excludedTargetsCount ? 'warn' : 'info'}>{excludedTargetsCount} removidos</Badge>
                </div>
                <div className="mt-3 space-y-2">
                  {availableExclusionBases.map((base) => {
                    const checked = excludedBaseIds.includes(base.id);
                    return (
                      <label
                        key={base.id}
                        className="flex cursor-pointer items-center justify-between gap-3 rounded-[var(--s1-radius-nav)] border px-3 py-2 text-sm"
                        style={{
                          background: checked ? 'var(--s1-accent-soft)' : 'var(--s1-field)',
                          borderColor: checked ? 'var(--s1-accent)' : 'var(--s1-field-border)',
                          color: 'var(--s1-text)',
                        }}
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-semibold">{base.name}</span>
                          <span className="block text-xs" style={{ color: 'var(--s1-muted)' }}>
                            {base.totalPhones || base.phones?.length || 0} leads
                          </span>
                        </span>
                        <input type="checkbox" checked={checked} onChange={() => toggleExcludedBase(base.id)} />
                      </label>
                    );
                  })}
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="rounded-[var(--s1-radius-nav)] border p-2" style={{ borderColor: 'var(--s1-inner-border)' }}>
                    <div className="font-semibold" style={{ color: 'var(--s1-text)' }}>{phones.length}</div>
                    <div style={{ color: 'var(--s1-muted)' }}>originais</div>
                  </div>
                  <div className="rounded-[var(--s1-radius-nav)] border p-2" style={{ borderColor: 'var(--s1-inner-border)' }}>
                    <div className="font-semibold" style={{ color: '#fbbf24' }}>{excludedTargetsCount}</div>
                    <div style={{ color: 'var(--s1-muted)' }}>excluídos</div>
                  </div>
                  <div className="rounded-[var(--s1-radius-nav)] border p-2" style={{ borderColor: 'var(--s1-inner-border)' }}>
                    <div className="font-semibold" style={{ color: 'var(--s1-accent-text)' }}>{filteredPhones.length}</div>
                    <div style={{ color: 'var(--s1-muted)' }}>receberão</div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between gap-3">
                <Label>Telefones</Label>
                <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-semibold" style={{ color: 'var(--s1-accent-text)' }}>
                  <FileArrowUp size={15} />
                  CSV/TXT
                  <input className="hidden" type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(e) => handleBaseFile(e.target.files?.[0])} />
                </label>
              </div>
              <Textarea
                rows={11}
                value={rawPhones}
                onChange={(event) => setRawPhones(event.target.value)}
                placeholder="Cole telefones, um por linha. Ex: 5562999999999"
                style={{ background: 'var(--s1-field)', borderColor: 'var(--s1-field-border)', color: 'var(--s1-text)' }}
              />
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" style={{ color: 'var(--s1-muted)' }}>
                <Badge variant="success">{phones.length} únicos válidos</Badge>
                {excludedTargetsCount > 0 && <Badge variant="warn">{filteredPhones.length} receberão</Badge>}
                <span>DDD + número, preferencialmente com 55.</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Delay min. (s)</Label>
                <Input type="number" min="60" value={minDelay} onChange={(e) => setMinDelay(e.target.value)} />
              </div>
              <div>
                <Label>Delay máx. (s)</Label>
                <Input type="number" min="60" value={maxDelay} onChange={(e) => setMaxDelay(e.target.value)} />
              </div>
              <div>
                <Label>Lote</Label>
                <Input type="number" min="1" value={batchSize} onChange={(e) => setBatchSize(e.target.value)} />
              </div>
              <div>
                <Label>Pausa lote (min)</Label>
                <Input type="number" min="0" value={batchPause} onChange={(e) => setBatchPause(e.target.value)} />
              </div>
              <div>
                <Label>Começa</Label>
                <Input type="time" value={windowStart} onChange={(e) => setWindowStart(e.target.value)} />
              </div>
              <div>
                <Label>Para</Label>
                <Input type="time" value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)} />
              </div>
            </div>

            <div className="rounded-[var(--s1-radius-inner)] border p-4 text-sm" style={{ background: 'var(--s1-inner)', borderColor: 'var(--s1-inner-border)', color: 'var(--s1-text-2)' }}>
              <div className="flex items-center gap-2 font-semibold" style={{ color: 'var(--s1-text)' }}>
                <ShieldCheck size={18} style={{ color: 'var(--s1-accent)' }} />
                Regras aplicadas
              </div>
              <p className="mt-2">
                Estimativa aproximada: {estimatedMinutes || 0} min para {filteredPhones.length} contatos, sem contar períodos fora da janela de horário.
              </p>
            </div>
          </div>
        </section>

        <section className="s1-card min-w-0 overflow-hidden rounded-[var(--s1-radius-card)] border p-4 sm:p-6" style={{ background: 'var(--s1-card)', borderColor: 'var(--s1-card-border)' }}>
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
            <div>
              <h2 className="font-display text-[24px] font-medium" style={{ color: 'var(--s1-text)' }}>Sequência</h2>
              <p className="mt-1 text-sm" style={{ color: 'var(--s1-muted)' }}>Texto, imagem, vídeo, áudio ou arquivo em uma única programação.</p>
            </div>
            <Badge>{finalItems.length} partes</Badge>
          </div>

          {finalItems.length > 0 && (
            <div className="mt-5 space-y-3">
              {finalItems.map((item, index) => (
                <div
                  key={`${itemTitle(item)}-${index}`}
                  className="flex min-w-0 items-start gap-3 rounded-[var(--s1-radius-inner)] border p-4"
                  style={{ background: 'var(--s1-inner)', borderColor: 'var(--s1-inner-border)' }}
                >
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-bold" style={{ background: 'var(--s1-accent)', color: '#fff' }}>
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold" style={{ color: 'var(--s1-text)' }}>{itemTitle(item)}</p>
                    <p className="mt-1 max-h-24 overflow-y-auto whitespace-pre-wrap break-words pr-2 text-sm" style={{ color: 'var(--s1-text-2)', overflowWrap: 'anywhere' }}>
                      {firstLine(item)}
                    </p>
                  </div>
                  {index < items.length && (
                    <button type="button" onClick={() => removeItem(index)} className="text-red-400 hover:text-red-300">
                      <Trash size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
            {TYPE_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setDraftType(option.id)}
                className="rounded-[var(--s1-radius-nav)] border px-3 py-3 text-sm font-semibold"
                style={{
                  background: draft.type === option.id ? 'var(--s1-accent-soft)' : 'var(--s1-field)',
                  borderColor: draft.type === option.id ? 'var(--s1-accent)' : 'var(--s1-field-border)',
                  color: draft.type === option.id ? 'var(--s1-accent-text)' : 'var(--s1-text-2)',
                }}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="mt-5 space-y-4">
            {draft.type === 'text' ? (
              <div>
                <Label>Mensagem</Label>
                <Textarea
                  rows={7}
                  value={draft.text}
                  onChange={(event) => setDraft((prev) => ({ ...prev, text: event.target.value }))}
                  placeholder="Escreva a mensagem..."
                  style={{ background: 'var(--s1-field)', borderColor: 'var(--s1-field-border)', color: 'var(--s1-text)' }}
                />
              </div>
            ) : (
              <>
                <div>
                  <Label>Arquivo</Label>
                  <label
                    className="flex cursor-pointer items-center justify-center gap-2 rounded-[var(--s1-radius-inner)] border border-dashed px-4 py-5 text-sm font-semibold"
                    style={{ background: 'var(--s1-field)', borderColor: 'var(--s1-field-border)', color: 'var(--s1-text-2)' }}
                  >
                    {uploading ? <Spinner /> : <UploadSimple size={18} />}
                    {draft.fileName || 'Escolher arquivo'}
                    <input className="hidden" type="file" onChange={(e) => handleMediaFile(e.target.files?.[0])} />
                  </label>
                </div>
                <div>
                  <Label>URL ou base64</Label>
                  <Textarea
                    rows={3}
                    value={draft.media}
                    onChange={(event) => setDraft((prev) => ({ ...prev, media: event.target.value }))}
                    placeholder="Cole URL/base64 ou use o seletor acima"
                    style={{ background: 'var(--s1-field)', borderColor: 'var(--s1-field-border)', color: 'var(--s1-text)' }}
                  />
                </div>
                <div>
                  <Label>Legenda opcional</Label>
                  <Textarea
                    rows={4}
                    value={draft.caption}
                    onChange={(event) => setDraft((prev) => ({ ...prev, caption: event.target.value }))}
                    placeholder="Texto que acompanha a mídia..."
                    style={{ background: 'var(--s1-field)', borderColor: 'var(--s1-field-border)', color: 'var(--s1-text)' }}
                  />
                </div>
              </>
            )}

            <div className="flex justify-end">
              <Button type="button" variant="secondary" onClick={addDraft}>
                <Plus size={16} /> Adicionar à sequência
              </Button>
            </div>

            <div className="border-t pt-5" style={{ borderColor: 'var(--s1-line-soft)' }}>
              <Label>Agendar início opcional</Label>
              <DateTimePicker value={scheduledAt} onChange={setScheduledAt} />
              <button
                type="button"
                onClick={() => setScheduledAt(localNowIso())}
                className="mt-2 inline-flex items-center gap-2 text-xs font-semibold"
                style={{ color: 'var(--s1-accent-text)' }}
              >
                <Clock size={15} /> usar horário atual
              </button>
            </div>

            <Button type="button" size="lg" className="w-full" onClick={submit} disabled={submitting || uploading}>
              {submitting ? <Spinner /> : <PaperPlaneTilt size={18} />}
              Criar broadcast para {filteredPhones.length} contatos
            </Button>
          </div>
        </section>
      </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, tone = 'default' }) {
  const color = tone === 'danger' ? '#f87171' : 'var(--s1-text)';
  return (
    <div className="rounded-[var(--s1-radius-nav)] border px-3 py-2" style={{ borderColor: 'var(--s1-inner-border)', background: 'var(--s1-card)' }}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: 'var(--s1-muted)' }}>{label}</p>
      <p className="mt-1 font-display text-xl" style={{ color }}>{value}</p>
    </div>
  );
}

function MediaThumb({ item }) {
  const src = item.media || item.mediaPreview || '';
  const isImage = item.mediatype === 'image';
  const isVideo = item.mediatype === 'video';
  const isAudio = item.type === 'audio';
  if (isAudio) {
    return (
      <div className="flex h-20 items-center gap-2 rounded-[var(--s1-radius-inner)] border px-3" style={{ borderColor: 'var(--s1-field-border)', background: 'var(--s1-field)' }}>
        <span className="grid h-9 w-9 place-items-center rounded-full" style={{ background: 'var(--s1-accent)', color: '#fff' }}>
          <Play size={15} weight="fill" />
        </span>
        <div className="flex flex-1 items-end gap-1">
          {Array.from({ length: 12 }).map((_, i) => (
            <span key={i} className="w-1 rounded-full" style={{ height: `${12 + ((i * 17) % 34)}px`, background: 'var(--s1-accent-soft)' }} />
          ))}
        </div>
      </div>
    );
  }
  if ((isImage || isVideo) && src && !src.startsWith('data:')) {
    return (
      <div className="relative h-20 overflow-hidden rounded-[var(--s1-radius-inner)] border" style={{ borderColor: 'var(--s1-field-border)', background: 'var(--s1-field)' }}>
        <img src={src} alt={item.fileName || 'prévia'} className="h-full w-full object-cover" />
        {isVideo && (
          <span className="absolute inset-0 grid place-items-center bg-black/20">
            <span className="grid h-8 w-8 place-items-center rounded-full bg-black/60 text-white">
              <Play size={15} weight="fill" />
            </span>
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="grid h-20 place-items-center rounded-[var(--s1-radius-inner)] border text-xs font-mono" style={{ borderColor: 'var(--s1-field-border)', background: 'var(--s1-field)', color: 'var(--s1-muted)' }}>
      sem prévia
    </div>
  );
}
